/**
 * The workspace document: one Y.Doc holding the whole tree (notes, folders,
 * titles, tags, ordering, trash state). Persisted to IndexedDB, mirrored
 * across tabs via BroadcastChannel, and — when signed in — synced to the
 * server (wired in the sync phase).
 *
 * INVARIANT: item fields live in a Y.Map per item so concurrent edits to
 * different fields (rename vs move) merge instead of clobbering.
 */
import * as Y from "yjs";
import { IndexeddbPersistence } from "y-indexeddb";
import {
  DOC_KEYS,
  TRASH_RETENTION_DAYS,
  archiveSubtree,
  newId,
  createItemIn,
  childrenOf as docChildrenOf,
  getItem as docGetItem,
  subtreeIds as docSubtreeIds,
  moveItem,
  readItems,
  renameItem,
  restoreTrashedSubtree,
  setItemTags,
  trashSubtree,
  unarchiveSubtree,
  updateItem,
  type ItemType,
  type WorkspaceItem,
} from "@hydra-notes/shared";
import { crossTabSync } from "./crosstab";

export const LOCAL_WORKSPACE_ID = "local";

type ItemMap = Y.Map<unknown>;

export interface WorkspaceStoreOpts {
  /**
   * false = no IndexedDB (shared folders are online-only; nothing may be
   * cached locally). whenLoaded is then resolved externally via markLoaded()
   * once the sync provider reports the first server sync.
   */
  persist?: boolean;
  /** BroadcastChannel name override (shared folders use hn.sf.<id>). */
  channelName?: string;
  /**
   * Purge expired trash on load. Off for shared folders: trash there is
   * owner-only, so only the owner's mount runs the purge (after sync).
   */
  purgeTrashOnLoad?: boolean;
}

export class WorkspaceStore {
  readonly doc: Y.Doc;
  readonly items: Y.Map<ItemMap>;
  readonly persistence: IndexeddbPersistence | null;
  readonly whenLoaded: Promise<void>;
  private markLoadedFn: () => void = () => {};

  private snapshot: WorkspaceItem[] = [];
  private snapshotDirty = true;
  private listeners = new Set<() => void>();
  private releaseCrossTab: () => void;

  constructor(
    readonly workspaceId: string,
    opts?: WorkspaceStoreOpts,
  ) {
    const persist = opts?.persist ?? true;
    const purge = opts?.purgeTrashOnLoad ?? true;
    this.doc = new Y.Doc({ gc: true });
    this.items = this.doc.getMap<ItemMap>(DOC_KEYS.workspaceItems);
    if (persist) {
      this.persistence = new IndexeddbPersistence(`hn.ws.${workspaceId}`, this.doc);
      this.whenLoaded = this.persistence.whenSynced.then(() => {
        if (purge) this.emptyTrash();
      });
    } else {
      this.persistence = null;
      this.whenLoaded = new Promise<void>((r) => (this.markLoadedFn = r));
    }
    this.releaseCrossTab = crossTabSync(opts?.channelName ?? `hn.ws.${workspaceId}`, this.doc);
    this.items.observeDeep(() => {
      this.snapshotDirty = true;
      for (const l of this.listeners) l();
    });
  }

  /** For persist-less stores: called when the sync provider first syncs. */
  markLoaded(): void {
    this.markLoadedFn();
  }

  destroy() {
    this.releaseCrossTab();
    this.persistence?.destroy();
    this.doc.destroy();
  }

  // ---------- reactive read API ----------

  subscribe = (cb: () => void): (() => void) => {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  };

  getSnapshot = (): WorkspaceItem[] => {
    // cached because the tree re-reads this on every render
    if (this.snapshotDirty) {
      this.snapshot = readItems(this.doc);
      this.snapshotDirty = false;
    }
    return this.snapshot;
  };

  getItem(id: string): WorkspaceItem | undefined {
    return docGetItem(this.doc, id);
  }

  /** Live (non-trashed, non-archived) children of a parent, sorted. */
  childrenOf(parentId: string | null): WorkspaceItem[] {
    return this.getSnapshot().filter((i) => i.parentId === parentId && !i.trashedAt && !i.archivedAt);
  }

  trashedItems(): WorkspaceItem[] {
    return this.getSnapshot().filter((i) => !!i.trashedAt);
  }

  archivedItems(): WorkspaceItem[] {
    return this.getSnapshot().filter((i) => !!i.archivedAt && !i.trashedAt);
  }

  // ---------- mutations ----------

  createItem(type: ItemType, parentId: string | null, title = ""): string {
    const id = newId();
    createItemIn(this.doc, id, type, parentId, title);
    return id;
  }

  rename(id: string, title: string) {
    renameItem(this.doc, id, title);
  }

  setTags(id: string, tags: string[]) {
    setItemTags(this.doc, id, tags);
  }

  setAliases(id: string, aliases: string[]) {
    this.update(id, (m) => m.set("aliases", aliases));
  }

  setPinned(id: string, pinned: boolean) {
    this.update(id, (m) => {
      if (pinned) m.set("pinned", true);
      else m.delete("pinned");
    });
  }

  /** Task lists: accent color key (rendered via --tl-<key> tokens). */
  setColorKey(id: string, colorKey: string | undefined) {
    this.update(id, (m) => {
      if (colorKey === undefined) m.delete("colorKey");
      else m.set("colorKey", colorKey);
    });
  }

  /** Mark or clear the builtin flag (the undeletable default task list). */
  setBuiltin(id: string, value: "tasks" | undefined) {
    this.update(id, (m) => {
      if (value === undefined) m.delete("builtin");
      else m.set("builtin", value);
    });
  }

  setFolderNote(id: string, folderNoteId: string | undefined) {
    this.update(id, (m) => {
      if (folderNoteId === undefined) m.delete("folderNoteId");
      else m.set("folderNoteId", folderNoteId);
    });
  }

  touch(id: string) {
    this.update(id, () => {});
  }

  /** Move under a new parent and/or between two siblings. */
  move(id: string, parentId: string | null, beforeId?: string | null) {
    moveItem(this.doc, id, parentId, beforeId);
  }

  /** Change only parent membership, preserving lifecycle state and order. */
  reparent(id: string, parentId: string | null) {
    this.update(id, (m) => m.set("parentId", parentId));
  }

  trash(id: string) {
    trashSubtree(this.doc, id);
  }

  /** Archive the whole subtree. Never auto-clears (unlike trash). */
  archive(id: string) {
    archiveSubtree(this.doc, id);
  }

  unarchive(id: string) {
    unarchiveSubtree(this.doc, id);
  }

  restore(id: string) {
    restoreTrashedSubtree(this.doc, id);
  }

  deletePermanent(id: string): string[] {
    const ids = this.subtreeIds(id);
    this.doc.transact(() => {
      for (const itemId of ids) this.items.delete(itemId);
    });
    return ids; // caller destroys the note docs
  }

  /** Purge trash entries older than retention. Runs on load. */
  emptyTrash(olderThanDays = TRASH_RETENTION_DAYS): string[] {
    const cutoff = Date.now() - olderThanDays * 24 * 60 * 60 * 1000;
    const expired = this.getSnapshot().filter((i) => i.trashedAt && i.trashedAt < cutoff);
    const all: string[] = [];
    for (const item of expired) {
      // only delete roots of trashed subtrees (children go with them)
      all.push(...this.deletePermanent(item.id));
    }
    return all;
  }

  // ---------- helpers ----------

  private update(id: string, fn: (m: ItemMap) => void) {
    updateItem(this.doc, id, fn);
  }

  private subtreeIds(rootId: string): string[] {
    return docSubtreeIds(this.doc, rootId);
  }
}
